import { useEffect, useState } from 'react'
import { FastAverageColor } from 'fast-average-color'

const fac = new FastAverageColor()

const colorCache = new Map<string, string | null>()

type RGB = {
  r: number
  g: number
  b: number
}

type HSL = {
  h: number
  s: number
  l: number
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const toHex = (value: number) =>
  Math.round(clamp(value, 0, 255))
    .toString(16)
    .padStart(2, '0')

const rgbToHex = ({ r, g, b }: RGB) => `#${toHex(r)}${toHex(g)}${toHex(b)}`

const mixRgb = (a: RGB, b: RGB, amount: number): RGB => ({
  r: a.r + (b.r - a.r) * amount,
  g: a.g + (b.g - a.g) * amount,
  b: a.b + (b.b - a.b) * amount,
})

const getLuminance = ({ r, g, b }: RGB) => 0.2126 * r + 0.7152 * g + 0.0722 * b

const rgbToHsl = ({ r, g, b }: RGB): HSL => {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0

  if (delta !== 0) {
    if (max === r) {
      h = 60 * (((g - b) / delta) % 6)
    } else if (max === g) {
      h = 60 * ((b - r) / delta + 2)
    } else {
      h = 60 * ((r - g) / delta + 4)
    }
  }

  if (h < 0) {
    h += 360
  }

  const l = (max + min) / 2

  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  return {
    h,
    s: s * 100,
    l: l * 100,
  }
}

const hslToRgb = ({ h, s, l }: HSL): RGB => {
  s /= 100
  l /= 100

  const c = (1 - Math.abs(2 * l - 1)) * s

  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))

  const m = l - c / 2

  let r = 0
  let g = 0
  let b = 0

  if (h >= 0 && h < 60) {
    r = c
    g = x
  } else if (h < 120) {
    r = x
    g = c
  } else if (h < 180) {
    g = c
    b = x
  } else if (h < 240) {
    g = x
    b = c
  } else if (h < 300) {
    r = x
    b = c
  } else {
    r = c
    b = x
  }

  return {
    r: (r + m) * 255,
    g: (g + m) * 255,
    b: (b + m) * 255,
  }
}

/**
 * Approximation of the way iOS Contacts derives
 * a muted background color from a contact image.
 *
 * Important behavior:
 * - preserves source hue
 * - strongly reduces saturation
 * - keeps naturally dark images very dark
 * - prevents bright images from becoming white
 * - dominant color has only a small influence
 */
const normalizeIosContactColor = (average: RGB, dominant: RGB): RGB => {
  const sourceLuminance = getLuminance(average)

  /**
   * Average represents the whole image.
   * Dominant only slightly nudges the hue.
   *
   * This is important for images like the FLOU logo:
   * the gold logo should not turn the whole background gold.
   */
  const mixed = mixRgb(average, dominant, 0.12)

  const hsl = rgbToHsl(mixed)

  /**
   * iOS backgrounds are generally much more muted
   * than the source image.
   */
  let saturation = hsl.s * 0.42

  saturation = clamp(saturation, 5, 24)

  /**
   * Preserve dark images.
   *
   * This is the biggest difference from the previous
   * implementation.
   */
  let lightness: number

  if (sourceLuminance < 35) {
    lightness = 4.5
  } else if (sourceLuminance < 50) {
    lightness = 7
  } else if (sourceLuminance < 70) {
    lightness = 11
  } else if (sourceLuminance < 95) {
    lightness = 18
  } else if (sourceLuminance < 125) {
    lightness = 26
  } else if (sourceLuminance < 155) {
    lightness = 33
  } else if (sourceLuminance < 190) {
    lightness = 39
  } else if (sourceLuminance < 220) {
    lightness = 43
  } else {
    lightness = 46
  }

  /**
   * Almost grayscale images should remain neutral.
   */
  if (hsl.s < 5) {
    saturation = 0
  }

  return hslToRgb({
    h: hsl.h,
    s: saturation,
    l: lightness,
  })
}

const extractIosContactColor = async (imageSrc: string): Promise<string | null> => {
  const cached = colorCache.get(imageSrc)

  if (cached !== undefined) {
    return cached
  }

  try {
    /**
     * Overall visual tone.
     */
    const averageResult = await fac.getColorAsync(imageSrc, {
      algorithm: 'sqrt',
      mode: 'precision',
    })

    /**
     * Main identifiable color.
     */
    const dominantResult = await fac.getColorAsync(imageSrc, {
      algorithm: 'dominant',
      mode: 'precision',
    })

    const average: RGB = {
      r: averageResult.value[0],
      g: averageResult.value[1],
      b: averageResult.value[2],
    }

    const dominant: RGB = {
      r: dominantResult.value[0],
      g: dominantResult.value[1],
      b: dominantResult.value[2],
    }

    const iosColor = normalizeIosContactColor(average, dominant)

    const hex = rgbToHex(iosColor)

    colorCache.set(imageSrc, hex)

    return hex
  } catch {
    return null
  }
}

const useDominantImageColor = (imageSrc?: string | null): string | null => {
  const [color, setColor] = useState<string | null>(() => {
    if (!imageSrc) {
      return null
    }

    return colorCache.get(imageSrc) ?? null
  })

  useEffect(() => {
    if (!imageSrc) {
      setColor(null)
      return
    }

    const cached = colorCache.get(imageSrc)

    if (cached !== undefined) {
      setColor(cached)
      return
    }

    let cancelled = false

    extractIosContactColor(imageSrc).then((result) => {
      if (!cancelled) {
        setColor(result)
      }
    })

    return () => {
      cancelled = true
    }
  }, [imageSrc])

  return color
}

export default useDominantImageColor
